// src/dashboard-cliente/ListarPagamentos.js
import React, { useState, useEffect } from 'react';
import "./estilodashuser.css";
import { useAuth } from '../auth';
import api from '../api';

const ListarPagamentos = () => {
  const { user } = useAuth();
  const [pagamentos, setPagamentos] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [mensagem, setMensagem] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchDados();
    }
  }, [user]);

  const fetchDados = async () => {
    try {
      const [respCliente, respPagamentos, respPlanos] = await Promise.all([
        api.get(`/api/clientes/${user.id}`),
        api.get('/api/pagamentos'),
        api.get('/api/planos-ativos'),
      ]);

      setCliente(respCliente.data);

      // Garante comparação numérica por segurança
      const listaPagamentosCliente = (respPagamentos.data || []).filter(
        (p) => Number(p.cliente_id) === Number(user.id)
      );

      setPagamentos(listaPagamentosCliente);
      setPlanos(respPlanos.data || []);
      setMensagem('');
    } catch (error) {
      console.error('Erro ao obter dados de pagamentos:', error);
      setMensagem('Erro ao carregar pagamentos.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const dia = d.getDate().toString().padStart(2, '0');
    const mes = (d.getMonth() + 1).toString().padStart(2, '0');
    const ano = d.getFullYear();
    return `${dia}-${mes}-${ano}`;
  };

  /**
   * Gera a lista de meses no formato YYYY-MM para exibir na tabela:
   * - Começa no MAIOR entre:
   *    a) mês de criação do cliente
   *    b) mês atual - 5 meses  (últimos ~6 meses)
   * - Termina em (mês atual + 2 meses).
   * Assim, nunca mostra mês anterior à existência do cliente.
   */
  const gerarMesesReferencia = (dataCriacao) => {
    if (!dataCriacao) return [];

    const hoje = new Date();
    const dataCriacaoDate = new Date(dataCriacao);
    if (Number.isNaN(dataCriacaoDate.getTime())) {
      // fallback: usar só os últimos 6 meses + 2 pra frente
      const listaFallback = [];
      const fimFallback = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      fimFallback.setMonth(fimFallback.getMonth() + 2);

      const inicioFallback = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      inicioFallback.setMonth(inicioFallback.getMonth() - 5);

      const atualFallback = new Date(inicioFallback);
      while (atualFallback <= fimFallback) {
        listaFallback.push(
          `${atualFallback.getFullYear()}-${(atualFallback.getMonth() + 1)
            .toString()
            .padStart(2, '0')}`
        );
        atualFallback.setMonth(atualFallback.getMonth() + 1);
      }
      return listaFallback;
    }

    // Normaliza criação e hoje para primeiro dia do mês
    const criacaoMes = new Date(
      dataCriacaoDate.getFullYear(),
      dataCriacaoDate.getMonth(),
      1
    );
    const hojeMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

    // Fim: hoje + 2 meses
    const fim = new Date(hojeMes);
    fim.setMonth(fim.getMonth() + 2);

    // Início base: hoje - 5 meses (últimos 6 meses)
    const inicioBase = new Date(hojeMes);
    inicioBase.setMonth(inicioBase.getMonth() - 5);

    // Início real: não pode ser antes da criação do cliente
    const inicio =
      criacaoMes > inicioBase ? new Date(criacaoMes) : new Date(inicioBase);

    const lista = [];
    const atual = new Date(inicio);
    while (atual <= fim) {
      lista.push(
        `${atual.getFullYear()}-${(atual.getMonth() + 1)
          .toString()
          .padStart(2, '0')}`
      );
      atual.setMonth(atual.getMonth() + 1);
    }

    return lista;
  };

  // Mapeia pagamentos por mes_referencia para lookup rápido
  const pagamentosMap = {};
  pagamentos.forEach((p) => {
    if (p.mes_referencia) {
      pagamentosMap[p.mes_referencia] = p;
    }
  });

  const calcularValorMensalPlano = () => {
    if (!cliente) return { texto: '—', valor: 0 };

    const plano = planos.find((p) => Number(p.id) === Number(cliente.plano_id));
    const slotsContratados =
      cliente.slots_contratados != null
        ? Number(cliente.slots_contratados)
        : 2;

    // Se não houver plano salvo, usa fallback (mesma lógica do admin)
    if (!plano) {
      let valor = 19.9;
      if (slotsContratados > 2) {
        valor = 19.9 + (slotsContratados - 2) * 9.9;
      }
      return {
        texto: `Plano padrão (2 slots base)`,
        valor,
      };
    }

    const slotsIncluidos =
      plano.slots_inclusos != null ? Number(plano.slots_inclusos) : 2;
    const valorBase =
      plano.valor_base != null ? Number(plano.valor_base) : 0;
    const valorExtraSlot =
      plano.valor_slot_extra != null ? Number(plano.valor_slot_extra) : 0;

    const extras = Math.max(slotsContratados - slotsIncluidos, 0);
    const valorTotal = valorBase + extras * valorExtraSlot;

    return {
      texto: `${plano.nome} — ${slotsContratados} slots contratados (inclui ${slotsIncluidos}, extras: ${extras})`,
      valor: valorTotal,
    };
  };

  const { texto: resumoPlano, valor: valorMensalEstimado } =
    calcularValorMensalPlano();

  if (!cliente) {
    return (
      <div className="user-payments-wrap">
        <div className="user-payments-card">
          <div className="user-payments-header">
            <h3>Meus Pagamentos</h3>
          </div>

          {mensagem && <p className="user-payments-alert user-payments-alert-error">{mensagem}</p>}
          {!mensagem && <p className="user-payments-muted">Carregando informações do cliente...</p>}
        </div>
      </div>
    );
  }

  const mesesReferencia = gerarMesesReferencia(cliente.created_on);

  // ✅ flags para isenção total (cliente.isento=1) vs isenção por mês (pagamento.valor=0 ou status isento)
  const clienteIsentoTotal = String(cliente.isento) === "1";

  return (
    <div className="user-payments-wrap">
      <div className="user-payments-card">
        <div className="user-payments-header">
          <h3>Meus Pagamentos</h3>
        </div>

        {mensagem && (
          <p className="user-payments-alert user-payments-alert-error">
            {mensagem}
          </p>
        )}

        <div className="user-payments-summary">
          <div className="user-payments-summary-line">
            <span className="user-payments-label">Plano atual</span>
            <span className="user-payments-value">{resumoPlano}</span>
          </div>

          <div className="user-payments-summary-line">
            <span className="user-payments-label">Valor mensal estimado</span>
            <span className="user-payments-value user-payments-money">
              R{' '}
              {valorMensalEstimado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        <div className="user-payments-note">
          * São exibidos os meses desde a sua contratação (data de criação do
          cadastro), considerando os últimos meses e alguns meses à frente para previsão.
        </div>

        <div className="user-payments-table-wrap">
          <table className="user-payments-table">
            <thead>
              <tr>
                <th>Mês</th>
                <th>Status</th>
                <th>Valor / Previsão</th>
              </tr>
            </thead>
            <tbody>
              {mesesReferencia.map((mes) => {
                const pagamento = pagamentosMap[mes];
                const dataAtual = new Date();
                const dataMes = new Date(`${mes}-01`);

                let status = 'Em atraso';
                let color = 'red';
                let valorTexto = '—';

                // ===== 1) Existe pagamento no mês => este registro manda =====
                if (pagamento) {
                  const dataPag = formatDate(pagamento.data_pagamento);

                  // Isenção por mês pode ser por status "Isento" OU por valor 0
                  const statusLower = String(pagamento.status || "").toLowerCase();
                  const valorNum = pagamento.valor != null ? parseFloat(pagamento.valor) : NaN;

                  const isentoMes =
                    statusLower.includes("isento") ||
                    (!Number.isNaN(valorNum) && valorNum === 0);

                  const pagoMes = statusLower.includes("pago");

                  if (isentoMes) {
                    status = dataPag !== '—' ? `Pagamento isento em ${dataPag}` : 'Pagamento isento';
                    color = 'blue';
                    valorTexto = 'R$ 0,00 (isento)';
                  } else if (pagoMes) {
                    status = `Pago em ${dataPag}`;
                    color = 'green';
                    valorTexto = `Pago R$ ${Number(valorNum || 0).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  } else {
                    // existe registro mas não é pago/isento (ex: aguardando)
                    if (dataMes > dataAtual) {
                      status = 'Aguardando pagamento';
                      color = 'gray';
                    } else {
                      status = 'Aguardando pagamento';
                      color = 'gray';
                    }

                    // mostra valor do registro se vier; senão mostra previsão
                    if (!Number.isNaN(valorNum)) {
                      valorTexto = `Previsto: R$ ${Number(valorNum).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`;
                    } else if (valorMensalEstimado > 0) {
                      valorTexto = `Previsto: R$ ${valorMensalEstimado.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`;
                    }
                  }

                // ===== 2) Não existe pagamento no mês =====
                } else if (clienteIsentoTotal) {
                  // ✅ Isenção total do cliente: nunca mostra "Em atraso"
                  status = 'Pagamento isento';
                  color = 'blue';
                  valorTexto = 'R$ 0,00 (isento)';

                } else if (dataMes > dataAtual) {
                  // Mês futuro: previsão
                  status = 'Aguardando pagamento';
                  color = 'gray';
                  if (valorMensalEstimado > 0) {
                    valorTexto = `Previsto: R$ ${valorMensalEstimado.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  }

                } else {
                  // Mês passado sem pagamento registrado e cliente NÃO é isento total
                  status = 'Em atraso';
                  color = 'red';
                  if (valorMensalEstimado > 0) {
                    valorTexto = `Previsto: R$ ${valorMensalEstimado.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  }
                }

                // Só UI: converte a "cor" em classe para ficar bonito sem mudar lógica
                const statusClass =
                  color === 'green'
                    ? 'user-payments-status user-payments-status-paid'
                    : color === 'blue'
                    ? 'user-payments-status user-payments-status-free'
                    : color === 'gray'
                    ? 'user-payments-status user-payments-status-waiting'
                    : 'user-payments-status user-payments-status-late';

                return (
                  <tr key={mes}>
                    <td className="user-payments-month">{mes}</td>
                    <td className={statusClass}>{status}</td>
                    <td className="user-payments-valuecell">{valorTexto}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default ListarPagamentos;
